import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Table, 
  Badge, 
  Spinner, 
  Modal, 
  Button,
  OverlayTrigger,
  Tooltip 
} from 'react-bootstrap';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as ChartTooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  FaConciergeBell, 
  FaClock, 
  FaUsers, 
  FaRupeeSign,
  FaChartLine,
  FaCreditCard,
  FaCalendarDay,
  FaStar,
  FaHistory,
  FaEye,
  FaCalendarTimes,
  FaCalendarCheck,
  FaMoneyBillWave,
  FaUserTie,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaMapMarkerAlt,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import { FiTrendingUp, FiTrendingDown, FiMoreVertical } from 'react-icons/fi';

import useApi from '../../hooks/useApi';
import './VendorDashboard.css';

const VendorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState("");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState("");
  const { bearerAuth } = useApi();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (!dashboardData) return;

    const serviceCalendar = dashboardData?.service_calendar || {};
    const calendarDays = serviceCalendar.days || [];
    const todayKey = new Date().toISOString().slice(0, 10);
    const hasToday = calendarDays.some((day) => day?.date === todayKey);

    if (hasToday) {
      setSelectedCalendarDate(todayKey);
      return;
    }

    setSelectedCalendarDate(calendarDays[0]?.date || todayKey);
  }, [dashboardData]);

  const fetchDashboardData = async () => {
    try {
      setError("");
      const response = await bearerAuth("vendor/dashboard");
      if (response?.data?.status) {
        setDashboardData(response.data.data || {});
      } else {
        setDashboardData(null);
        setError(response?.data?.message || "Failed to load dashboard data.");
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      setDashboardData(null);
      setError("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  const handleBookingClick = async (bookingId) => {
    try {
      const response = await bearerAuth("vendor/service-bookings", "GET");
      const list = response?.data?.data || [];
      const booking = list.find((b) => Number(b.id) === Number(bookingId));
      if (booking) {
        setSelectedBooking(booking);
        setShowBookingModal(true);
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await bearerAuth(`vendor/service-bookings/${bookingId}/status`, "POST", { status: newStatus });
      setShowBookingModal(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger mt-4 d-flex align-items-center justify-content-between">
        <div>
          <FaCalendarTimes className="me-2" />
          {error}
        </div>
        <Button variant="outline-danger" size="sm" onClick={fetchDashboardData}>
          Retry
        </Button>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const data = dashboardData || {};
  const overview = data.overview || {};
  const recent_activities = data.recent_activities || [];
  const top_services = data.top_services || [];
  const monthly_earnings = data.monthly_earnings || [];
  const today_appointments = data.today_appointments || [];
  const todayCount = Array.isArray(today_appointments) ? today_appointments.length : 0;
  const upcoming_appointments = data.upcoming_appointments || [];
  const serviceCalendar = data.service_calendar || {};
  const calendarMonthKey = serviceCalendar.month || new Date().toISOString().slice(0, 7);
  const [calendarYearRaw, calendarMonthRaw] = calendarMonthKey.split("-");
  const calendarYear = Number(calendarYearRaw) || new Date().getFullYear();
  const calendarMonth = Number(calendarMonthRaw) || new Date().getMonth() + 1;
  const calendarMonthStart = new Date(calendarYear, calendarMonth - 1, 1);
  const calendarDaysInMonth = new Date(calendarYear, calendarMonth, 0).getDate();
  const calendarFirstWeekday = calendarMonthStart.getDay();
  const calendarCells = [
    ...Array(calendarFirstWeekday).fill(null),
    ...Array.from({ length: calendarDaysInMonth }, (_, index) => index + 1),
  ];
  const calendarMap = (serviceCalendar.days || []).reduce((acc, day) => {
    if (day?.date) acc[day.date] = day;
    return acc;
  }, {});
  const selectedDayBookings = calendarMap[selectedCalendarDate]?.items || [];
  const payment_method_stats = data.payment_method_stats || [];
  const vendor_student_commission = data.vendor_student_commission || {};
  const vendor_info = data.vendor_info || {};
  const num = (v) => Number(v || 0);

  // Stats Cards Configuration
 const statsCards = [
  {
    title: 'Total Services',
    value: num(overview.total_services),
    subtitle: `${num(overview.active_services)} Active`,
    icon: <i className="bi bi-bell fs-4"></i>,
    color: 'primary',
    gradient: 'gradient-primary',
  },
  {
    title: 'Pending Bookings',
    value: num(overview.pending_bookings),
    subtitle: `${num(overview.confirmed_bookings)} Confirmed`,
    icon: <i className="bi bi-clock fs-4"></i>,
    color: 'warning',
    gradient: 'gradient-warning',
  },
  {
    title: 'Active Customers',
    value: num(overview.active_customers),
    subtitle: `${num(overview.completed_bookings)} Completed`,
    icon: <i className="bi bi-people fs-4"></i>,
    color: 'success',
    gradient: 'gradient-success',
  },
  {
    title: 'Monthly Revenue',
    value: `\u20B9${num(overview.monthly_revenue).toLocaleString()}`,
    subtitle: `\u20B9${num(overview.total_revenue).toLocaleString()} Total`,
    icon: <i className="bi bi-currency-rupee fs-4"></i>,
    color: 'info',
    gradient: 'gradient-info',
  },
  {
    title: 'Student Commission',
    value: `\u20B9${num(vendor_student_commission.estimated_commission_total).toLocaleString()}`,
    subtitle: `${num(vendor_student_commission.billable_enrollments)} Billable Enrollments`,
    icon: <i className="bi bi-receipt-cutoff fs-4"></i>,
    color: 'dark',
    gradient: 'gradient-dark',
  },
];

  // Status Badge Variant
  const getStatusBadge = (status) => {
    const s = Number(status);
    if (s === 1) return 'success';
    if (s === 2) return 'danger';
    return 'warning';
  };

  const getStatusColor = (status) => {
    const s = Number(status);
    if (s === 1) return '#00C9A7';
    if (s === 2) return '#F64E60';
    return '#FFB822';
  };

  const getStatusLabel = (status) => {
    const s = Number(status);
    if (s === 1) return 'Completed';
    if (s === 2) return 'Cancelled';
    return 'Pending';
  };

  // Payment Method Colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Format currency
  const formatCurrency = (amount) => {
    const value = Number(amount || 0);
    return `\u20B9${value.toLocaleString('en-IN', {
      maximumFractionDigits: 2
    })}`;
  };

  const formatDateOnly = (value) => {
    if (!value) return "-";
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="vendor-dashboard">
      {/* 顶部欢迎栏 */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="vendor-welcome-title">Welcome back, {vendor_info?.name || vendor_info?.username}! </h1>
          <p className="vendor-subtitle">
  <i className="bi bi-building me-2"></i>
  {vendor_info.business_name || 'Your Business Dashboard'}

  {vendor_info.city && vendor_info.state && (
    <span className="ms-3">
      <i className="bi bi-geo-alt-fill me-1"></i>
      {vendor_info.city}, {vendor_info.state}
    </span>
  )}
</p>

        </div>
        <div className="d-flex align-items-center">
          {vendor_info.email && (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Email: {vendor_info.email}</Tooltip>}
            >
              <span className="me-3">
                <FaEnvelope className="me-1" />
              </span>
            </OverlayTrigger>
          )}
          {vendor_info.phone && (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Phone: {vendor_info.phone}</Tooltip>}
            >
              <span>
                <FaPhone className="me-1" />
              </span>
            </OverlayTrigger>
          )}
        </div>
      </div>

      {/* 统计卡片 */}
      <Row className="mb-4">
        {statsCards.map((stat, index) => (
          <Col lg={3} md={6} key={index} className="mb-4">
            <Card 
              className={`kt-vendor-card stats-card-gradient ${stat.gradient}`}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="stats-card-title">{stat.title}</div>
                    <div className="stats-card-value">{stat.value}</div>
                    <div className="stats-card-subtitle">{stat.subtitle}</div>
                    {stat.trend && (
                      <div className="d-flex align-items-center mt-2">
                        <span className="badge bg-white text-dark me-2">
                          {stat.trend > 0 ? (
                            <>
                              <FiTrendingUp className="me-1" />
                              +{stat.trend}%
                            </>
                          ) : (
                            <>
                              <FiTrendingDown className="me-1" />
                              {stat.trend}%
                            </>
                          )}
                        </span>
                        <small>from last month</small>
                      </div>
                    )}
                  </div>
                  <div className="stats-card-icon">
                    {stat.icon}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row>
        {/* 月度收入图表 */}
        <Col lg={8} className="mb-4">
          <Card className="kt-vendor-card">
           <Card.Header>
  <h5 className="mb-0">
    <i className="bi bi-graph-up-arrow card-icon text-primary me-2"></i>
    Revenue Trend (Last 6 Months)
  </h5>
</Card.Header>

            <Card.Body>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthly_earnings}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EFF2F5" />
                    <XAxis 
                      dataKey="month" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#7E8299' }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#7E8299' }}
                      tickFormatter={(value) => `₹${value}`}
                    />
                    <ChartTooltip 
                      formatter={(value, name) => {
                        const numericValue = Number(value || 0);
                        if (name === "Revenue") {
                          return [`₹${numericValue.toLocaleString('en-IN')}`, 'Revenue'];
                        }
                        return [numericValue, 'No. of Services'];
                      }}
                      contentStyle={{ 
                        borderRadius: '8px',
                        border: '1px solid #EFF2F5'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#3699FF" 
                      strokeWidth={3}
                      name="Revenue"
                      dot={{ r: 5, strokeWidth: 2, stroke: '#3699FF', fill: 'white' }}
                      activeDot={{ r: 7 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="booking_count" 
                      stroke="#00C9A7" 
                      strokeWidth={2}
                      name="No. of Services"
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* 支付方式饼图 */}
        <Col lg={4} className="mb-4">
          <Card className="kt-vendor-card">
           <Card.Header>
  <h5 className="mb-0">
    <i className="bi bi-credit-card-2-front card-icon text-success me-2"></i>
    Payment Methods Distribution
  </h5>
</Card.Header>

            <Card.Body>
              <div className="chart-container-small">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={payment_method_stats}
                      dataKey="count"
                      nameKey="payment_method"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {payment_method_stats.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <ChartTooltip 
                      formatter={(value, name) => [value, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3">
                {payment_method_stats.map((method, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center">
                      <div 
                        className="status-indicator" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span>{method.payment_method}</span>
                    </div>
                    <span className="fw-bold">{method.count}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 今日预约 */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="kt-vendor-card">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
               <h5 className="mb-0">
  <i className="bi bi-calendar-day card-icon text-success me-2"></i>
  Today's Appointments <span className="badge bg-light text-dark ms-2">{todayCount}</span>
</h5>

                <small className="text-muted">
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </small>
              </div>
            </Card.Header>
            <Card.Body>
              {todayCount > 0 ? (
                <div className="table-responsive">
                  <Table hover className="kt-vendor-table">
                    <thead>
                      <tr>
                        <th>Booking Code</th>
                        <th>Time</th>
                        <th>Customer</th>
                        <th>Service(s)</th>
                        <th>Phone</th>
                        <th>Amount</th>
                        <th>Vendor Payout</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {today_appointments.map((appointment, index) => (
                        <tr key={index}>
                          <td>
                            <strong>{appointment.booking_code}</strong>
                          </td>
                          <td>{appointment.scheduled_time}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <FaUserTie className="me-2 text-muted" />
                              {appointment.customer_name || appointment.contact_name}
                            </div>
                          </td>
                          <td>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  {appointment.service_names}
                                </Tooltip>
                              }
                            >
                              <span className="text-truncate d-inline-block" style={{ maxWidth: '150px' }}>
                                {appointment.service_names}
                              </span>
                            </OverlayTrigger>
                          </td>
                          <td>{appointment.contact_phone}</td>
                          <td className="amount-display">
                            {formatCurrency(appointment.total)}
                          </td>
                          <td className="amount-display">
                            {formatCurrency(
                              appointment.vendor_payout ??
                              (Number(appointment.total || 0) - Number(appointment.admin_commission || 0))
                            )}
                          </td>
                          <td>
                            <span className={`kt-vendor-badge badge-${getStatusBadge(appointment.status)}`}>
                              <span 
                                className="status-indicator" 
                                style={{ backgroundColor: getStatusColor(appointment.status) }}
                              />
                              {getStatusLabel(appointment.status)}
                            </span>
                          </td>
                          <td>
                            <button 
                              className="action-btn"
                              onClick={() => handleBookingClick(appointment.id)}
                            >
                              <FaEye size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="kt-vendor-empty">
                  <FaCalendarTimes className="kt-vendor-empty-icon" />
                  <h5 className="mt-3">No appointments scheduled for today</h5>
                  <p className="text-muted">All your appointments will appear here</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* 热门服务 */}
        <Col lg={12} className="mb-4">
          <Card className="kt-vendor-card">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="mb-0">
                    <i className="bi bi-calendar2-week card-icon text-primary me-2"></i>
                    Service Booking Calendar
                  </h5>
                  <small className="text-muted">
                    View service bookings date-wise for{" "}
                    {calendarMonthStart.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                  </small>
                </div>
                <span className="badge bg-light text-dark">
                  {selectedDayBookings.length} booking{selectedDayBookings.length === 1 ? "" : "s"} selected
                </span>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="row g-4">
                <div className="col-lg-5">
                  <div className="class-calendar-weekdays">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
                      <div key={label} className="class-calendar-weekday">
                        {label}
                      </div>
                    ))}
                  </div>
                  <div className="class-calendar-grid">
                    {calendarCells.map((day, index) => {
                      if (!day) {
                        return <div key={`empty-${index}`} className="class-calendar-cell empty" />;
                      }

                      const dateKey = `${calendarMonthKey}-${String(day).padStart(2, "0")}`;
                      const dayData = calendarMap[dateKey];
                      const isSelected = selectedCalendarDate === dateKey;

                      return (
                        <button
                          key={dateKey}
                          type="button"
                          className={`class-calendar-cell ${isSelected ? "selected" : ""} ${dayData?.total ? "has-classes" : ""}`}
                          onClick={() => setSelectedCalendarDate(dateKey)}
                        >
                          <span className="day">{day}</span>
                          {dayData?.total ? <span className="count">{dayData.total}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="col-lg-7">
                  <h6 className="mb-2">Bookings on {formatDateOnly(selectedCalendarDate)}</h6>
                  <div className="table-responsive">
                    <Table hover className="kt-vendor-table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Booking Code</th>
                          <th>Time</th>
                          <th>Customer</th>
                          <th>Services</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDayBookings.length > 0 ? (
                          selectedDayBookings.map((row) => (
                            <tr key={`calendar-${row.id}`}>
                              <td className="fw-semibold">{row.booking_code || row.id}</td>
                              <td>{row.scheduled_time || "-"}</td>
                              <td>{row.customer_name || "-"}</td>
                              <td>{row.service_names || "-"}</td>
                              <td>
                                <span className={`kt-vendor-badge badge-${getStatusBadge(row.status)}`}>
                                  {getStatusLabel(row.status)}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="text-muted py-4">
                              No bookings on this date.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card className="kt-vendor-card">
            <Card.Header>
              <h5>
                <FaStar className="card-icon text-warning" />
                Top Performing Services
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-container-small">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={top_services}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EFF2F5" vertical={false} />
                    <XAxis 
                      dataKey="service_name" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      tick={{ fill: '#7E8299', fontSize: 12 }}
                    />
                    <YAxis 
                      tick={{ fill: '#7E8299' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <ChartTooltip 
                      formatter={(value) => [value, 'Bookings']}
                    />
                    <Bar 
                      dataKey="booking_count" 
                      fill="#00C9A7" 
                      name="Total Bookings"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3">
                {top_services.map((service, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                    <div>
                      <strong>{service.service_name}</strong>
                      <div className="text-muted small">
                        {service.services_category} • {formatCurrency(service.price)}
                      </div>
                    </div>
                    <Badge bg="success" pill>
                      {service.booking_count} bookings
                    </Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* 最近预订 */}
        <Col lg={6} className="mb-4">
          <Card className="kt-vendor-card">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
  <i className="bi bi-clock-history card-icon text-info me-2"></i>
  Recent Bookings
</h5>

                <small className="text-muted">Last 7 days</small>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="timeline-scroll">
                {recent_activities.length > 0 ? (
                  recent_activities.map((activity, index) => (
                    <div 
                      key={index}
                      className="timeline-item p-3"
                      onClick={() => handleBookingClick(activity.id)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="timeline-content">
                          <div className="timeline-title">
                            {activity.booking_code} • {activity.customer_name}
                          </div>
                          <div className="timeline-service">
                            {activity.service_names}
                          </div>
                          <div className="timeline-amount">
                            {formatCurrency(activity.total)}
                          </div>
                        </div>
                        <div>
                          <span className={`kt-vendor-badge badge-${getStatusBadge(activity.status)}`}>
                            {getStatusLabel(activity.status)}
                          </span>
                        </div>
                      </div>
                      <div className="timeline-time mt-2">
                        <FaClock className="me-1" size={12} />
                        {new Date(activity.created_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="kt-vendor-empty p-4">
                    <FaHistory className="kt-vendor-empty-icon" />
                    <h5 className="mt-3">No recent bookings</h5>
                    <p className="text-muted">New bookings will appear here</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 预订详情模态框 */}
      <Modal 
        show={showBookingModal} 
        onHide={() => setShowBookingModal(false)} 
        size="lg"
        className="kt-vendor-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <div>
              <Row className="mb-4">
                <Col md={6}>
                  <h6 className="text-muted mb-2">CUSTOMER INFORMATION</h6>
                  <div className="d-flex align-items-center mb-3">
                    <FaUserTie className="me-3 text-primary" />
                    <div>
                      <strong>{selectedBooking.customer_name}</strong>
                      <div className="text-muted small">Customer</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-3">
                    <FaEnvelope className="me-3 text-primary" />
                    <div>
                      <strong>{selectedBooking.contact_email}</strong>
                      <div className="text-muted small">Email</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <FaPhone className="me-3 text-primary" />
                    <div>
                      <strong>{selectedBooking.contact_phone}</strong>
                      <div className="text-muted small">Phone</div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <h6 className="text-muted mb-2">BOOKING DETAILS</h6>
                  <div className="d-flex align-items-center mb-3">
                    <FaCalendarCheck className="me-3 text-success" />
                    <div>
                      <strong>{
                        selectedBooking?.scheduled_date
                          ? new Date(selectedBooking.scheduled_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })
                          : 'N/A'
                      } • {
                        selectedBooking?.scheduled_time
                          ? new Date(`1970-01-01T${selectedBooking.scheduled_time}`).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'N/A'
                      }</strong>
                      <div className="text-muted small">Date & Time</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-3">
                      <span className={`kt-vendor-badge badge-${getStatusBadge(selectedBooking.status)}`}>
                        {getStatusLabel(selectedBooking.status)}
                      </span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <FaCreditCard className="me-3 text-warning" />
                    <div>
                      <strong>{selectedBooking.payment_method}</strong>
                      <div className="text-muted small">Payment Method</div>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="kt-divider"></div>

              <h6 className="text-muted mb-3">SERVICES</h6>
              {(selectedBooking.services || []).length > 0 ? (
                selectedBooking.services.map((service, idx) => (
                  <div key={idx} className="service-item">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{service.service_name}</strong>
                      </div>
                      <div className="amount-display">
                        {formatCurrency(service.price)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="service-item">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{(selectedBooking.service_names || []).join(', ') || '-'}</strong>
                    </div>
                    <div className="amount-display">
                      {formatCurrency(selectedBooking.total || 0)}
                    </div>
                  </div>
                </div>
              )}

              <div className="kt-divider"></div>

              <Row className="mt-4">
                <Col md={3}>
                  <div className="text-muted small">Subtotal</div>
                  <div className="amount-display">{formatCurrency(selectedBooking.price)}</div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">Discount</div>
                  <div className="amount-display text-danger">-{formatCurrency(selectedBooking.discount)}</div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">Tax</div>
                  <div className="amount-display">{formatCurrency(selectedBooking.tax)}</div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">Total</div>
                  <div className="amount-display text-success fs-5">
                    {formatCurrency(selectedBooking.total)}
                  </div>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={3}>
                  <div className="text-muted small">Admin Commission</div>
                  <div className="amount-display text-danger">-{formatCurrency(selectedBooking.admin_commission || 0)}</div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">Vendor Payout</div>
                  <div className="amount-display text-success">
                    {formatCurrency(
                      selectedBooking.vendor_payout ??
                      (Number(selectedBooking.total || 0) - Number(selectedBooking.admin_commission || 0))
                    )}
                  </div>
                </Col>
              </Row>

              {selectedBooking.notes && (
                <>
                  <div className="kt-divider mt-4"></div>
                  <div className="mt-4">
                    <h6 className="text-muted mb-2">NOTES</h6>
                    <div className="p-3 bg-light rounded">
                      {selectedBooking.notes}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {Number(selectedBooking?.status) === 0 && (
            <>
              <Button 
                variant="success" 
                onClick={() => updateBookingStatus(selectedBooking.id, 1)}
              >
                Mark as Completed
              </Button>
              <Button 
                variant="danger" 
                onClick={() => updateBookingStatus(selectedBooking.id, 2)}
              >
                Cancel Booking
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={() => setShowBookingModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VendorDashboard;





